import { useState } from 'react';

import {
  getAssessorControllerObterSaldoQueryKey,
  useAssessorControllerReforcarSaldo,
} from '@/api/generated/assessoria/assessoria';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/http-client';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { toast } from '@/shared/ui/sonner';

const MINIMO = 1;
const MAXIMO = 5;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  saldoAtual: number;
  teto: number;
}

/**
 * Reforço manual do saldo semanal — só quando o saldo desta semana já foi
 * TODO gasto (`saldoAtual === 0`). A ideia é esgotar as vagas fixas antes de
 * pedir mais, não empilhar reforço em cima de saldo que ainda nem usou.
 */
export function ReforcarSaldoDialogo({ aberto, aoFechar, saldoAtual, teto }: Props) {
  const queryClient = useQueryClient();
  const reforcar = useAssessorControllerReforcarSaldo();
  const [quantidade, setQuantidade] = useState(1);
  const [erro, setErro] = useState<string | null>(null);

  const podeReforcar = saldoAtual === 0;
  const maximoPermitido = Math.min(MAXIMO, teto);

  async function aoConfirmar() {
    setErro(null);
    try {
      const atualizado = await reforcar.mutateAsync({ data: { quantidade } });
      queryClient.setQueryData(getAssessorControllerObterSaldoQueryKey(), atualizado);
      toast.success(`Saldo reforçado. Agora você tem ${atualizado.saldo} vaga(s) disponível(is).`);
      setQuantidade(1);
      aoFechar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível reforçar o saldo.');
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reforçar saldo</DialogTitle>
          <DialogDescription>
            {podeReforcar
              ? `Some vagas ao saldo desta semana, até ${maximoPermitido} de uma vez.`
              : 'Só dá para reforçar depois de gastar todas as vagas fixas da semana.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {erro && (
            <Alert variant="destructive">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          )}

          {!podeReforcar ? (
            <p className="text-sm text-muted-foreground">
              Você ainda tem {saldoAtual} vaga(s) disponível(is) nesta semana — gaste todas antes de
              reforçar.
            </p>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setQuantidade((q) => Math.max(MINIMO, q - 1))}
                disabled={quantidade <= MINIMO}
              >
                −
              </Button>
              <span className="w-10 text-center text-lg font-semibold text-foreground">{quantidade}</span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setQuantidade((q) => Math.min(maximoPermitido, q + 1))}
                disabled={quantidade >= maximoPermitido}
              >
                +
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
          {podeReforcar && (
            <Button onClick={() => void aoConfirmar()} disabled={reforcar.isPending}>
              Reforçar +{quantidade}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
